/**
 * Pumpkin's Halloween set, shown for the whole of October every year (see
 * src/lib/season.ts): the eight core poses re-drawn with her in a pumpkin
 * costume, plus a small bat companion that PumpkinFigure flies above her
 * head as a separate, animated sprite.
 *
 * Generated with Nano Banana Pro using each shipped original as the pose
 * reference, so she stays on-model (scripts/gen_halloween_pumpkin.py →
 * scripts/process_halloween_pumpkin.py). Each pose keeps its original's
 * width, centre and baseline in the 1024² frame, so Pumpkin renders at the
 * same size in costume; the stem hat adds height upward.
 *
 * As with the everyday set, the PNGs are the canonical source and the
 * WebPs are what ships (npm run assets:optimize-pumpkin).
 */
import idleDefault from "./idle-default.webp";
import waveHello from "./wave-hello.webp";
import pointPresent from "./point-present.webp";
import listeningAlert from "./listening-alert.webp";
import cheerCelebrate from "./cheer-celebrate.webp";
import gentleEncourage from "./gentle-encourage.webp";
import presentSticker from "./present-sticker.webp";
import curledRest from "./curled-rest.webp";
import batUp from "./bat-up.webp";
import batDown from "./bat-down.webp";
import batSleep from "./bat-sleep.webp";

export const halloweenPoses = {
  idleDefault,
  waveHello,
  pointPresent,
  listeningAlert,
  cheerCelebrate,
  gentleEncourage,
  presentSticker,
  curledRest,
} as const;

export type HalloweenPose = keyof typeof halloweenPoses;

/** Every core pose has a costume version; one-offs (schoolUniform) don't. */
export function isHalloweenPose(pose: string): pose is HalloweenPose {
  return pose in halloweenPoses;
}

/**
 * Bat frames, all on one shared 384² canvas so the body stays registered:
 * `up` and `down` alternate as the flap; `sleep` (wings wrapped, eyes
 * closed) is for the curled-up resting pose.
 */
export const bat = { up: batUp, down: batDown, sleep: batSleep } as const;

export interface BatPlacement {
  /** Bat centre, as % of Pumpkin's square frame from the left. */
  x: number;
  /** Bat centre, as % of the frame from the top. */
  y: number;
  /** Bat sprite width, as % of the frame width. */
  size: number;
  /** Use the sleeping bat (no flap) — for the curled-up pose. */
  asleep?: boolean;
}

/**
 * Where the bat flies on each pose — fitted to the art, so re-check these
 * if a pose is ever regenerated. It keeps to the same side (above her head,
 * viewer's right) on every pose so it's predictable, clear of her face,
 * paws, pointing arm and the held sticker in both flap frames, and inside
 * the frame at rest (the bob lifts it a little above the top). On the
 * curled-up pose it naps above her hat.
 */
export const batPlacement: Readonly<Record<HalloweenPose, BatPlacement>> = {
  idleDefault: { x: 82, y: 11, size: 32 },
  waveHello: { x: 82, y: 11, size: 32 },
  pointPresent: { x: 77, y: 12, size: 32 },
  listeningAlert: { x: 82, y: 11, size: 32 },
  cheerCelebrate: { x: 84, y: 11, size: 32 },
  gentleEncourage: { x: 82, y: 11, size: 32 },
  presentSticker: { x: 78, y: 13, size: 32 },
  curledRest: { x: 25, y: 9, size: 32, asleep: true },
};
